import CSSHelpers from "app/utils/css-helpers";
import FullScreen from "app/utils/fullscreen";
import EventTarget from "app/utils/event-target";
import PlayerEvents from "app/events/player-events";

const HIDDEN = "bvp-hidden";

/**
 * @class BaseView
 * @extends {EventTarget}
 */
class BaseView extends EventTarget {
    /**
     * @constructs BaseView
     */
    constructor() {
        super();
        this.el_ = this.createEl();
        this.childViews_ = [];
        this.isFullScreen = false;
    }

    /**
     * @method
     * @param {string} nodeType
     * @param {string} className
     * @param {object} attrs
     * @param {object} styles
     * @return {Element|*}
     */
    createEl(nodeType = "div", className = "", attrs = {}, styles = {}) {
        const element = document.createElement(nodeType);

        CSSHelpers.addClass(element, className);
        CSSHelpers.css(element, styles);

        Object.keys(attrs).reduce((el, attr) => {
            const attrValue = attrs[attr];

            if (el[attr] !== undefined) {
                el[attr] = attrValue;
            } else {
                el.setAttribute(attr, attrValue);
            }

            return el;
        }, element);

        return element;
    }

    /**
     * @method
     * @param {BaseView} view
     * @param {string} [to] Destination selector defaults to view element
     */
    appendView(view, to) {
        this.childViews_.push(view);
        const destination = (to ? this.find(to) : this.el_) || this.el_;

        destination.appendChild(view.el());
    }

    /**
     * @method
     * @param {string} className
     * @param {Element} el
     */
    addClass(className, el = this.el_) {
        CSSHelpers.addClass(el, className);
    }

    /**
     * @method
     * @param {string} className
     * @param {Element} el
     */
    removeClass(className, el = this.el_) {
        CSSHelpers.removeClass(el, className);
    }

    /**
     * @method
     * @param {string} selector
     * @returns {Element}
     */
    find(selector) {
        return this.el_.querySelector(selector);
    }

    /**
     * @method
     * @param {string} selector
     * @returns {NodeList}
     */
    findAll(selector) {
        return this.el_.querySelectorAll(selector);
    }

    /**
     * Overwrites element style
     * @method
     * @param {object} styleObject
     */
    css(styleObject) {
        CSSHelpers.css(this.el_, styleObject);
    }

    /**
     * @method
     * @returns {null|Element}
     */
    el() {
        return this.el_;
    }

    /**
     * @method
     */
    hide() {
        this.addClass(HIDDEN);
    }

    /**
     * @method
     */
    show() {
        this.removeClass(HIDDEN);
    }

    /**
     * @method
     */
    requestFullScreen() {
        if (this.fullScreenObserverIndex === undefined) {
            this.fullScreenObserverIndex = BaseView.addFullScreenObserver(this);
        }

        FullScreen.requestFullScreen(this.el_);
    }

    /**
     * @method
     */
    exitFullScreen() {
        if (this.fullScreenObserverIndex === undefined) {
            this.fullScreenObserverIndex = BaseView.addFullScreenObserver(this);
        }

        FullScreen.exitFullScreen();
    }

    /**
     * @override
     * @method
     */
    dispose() {
        this.el_ = null;
        this.childViews_.forEach((childView) => childView.dispose());
        this.childViews_ = null;

        if (this.fullScreenObserverIndex !== undefined) {
            BaseView.removeFullScreenObserver(this.fullScreenObserverIndex);
        }

        super.dispose();
    }

    /**
     * @static
     * @param {BaseView} baseViewInstance
     * @returns {number}
     */
    static addFullScreenObserver(baseViewInstance) {
        if (!FullScreen.isSupported()) {
            return;
        }

        BaseView.fullScreenObserversCount++;

        if (!BaseView.fullScreenObservers) {
            BaseView.fullScreenObservers = [];
            document.addEventListener(FullScreen.fullscreenchange, BaseView.onFullScreenChange);
        }

        return (BaseView.fullScreenObservers.push(baseViewInstance) - 1);
    }

    /**
     * @static
     * @param {number} index
     */
    static removeFullScreenObserver(index) {
        if (index == undefined || !FullScreen.isSupported()) {
            return;
        }

        BaseView.fullScreenObserversCount--;

        if (BaseView.fullScreenObserversCount <= 0) {
            document.removeEventListener(FullScreen.fullscreenchange, BaseView.onFullScreenChange);
            BaseView.fullScreenObservers = null;
            BaseView.fullScreenObserversCount = 0;
            return;
        }

        BaseView.fullScreenObservers[index] = null;
    }

    /**
     * We maintain a single event observer which loops over views instead of individual binding
     * @static
     */
    static onFullScreenChange() {
        if (!BaseView.fullScreenObservers) {
            return;
        }

        const target = FullScreen.getFullScreenElement();
        const isInFullScreen = FullScreen.isInFullScreen();
        const prunedList = [];

        for (let i = 0, ii = BaseView.fullScreenObservers.length; i < ii; i++) {
            const baseView = BaseView.fullScreenObservers[i];
            if (!baseView) {
                continue;
            }

            if (target === baseView.el_ || !target) {
                baseView.isFullScreen = isInFullScreen;
                baseView.trigger(PlayerEvents.FULL_SCREEN_CHANGE, { fullscreen: isInFullScreen });
            }

            baseView.fullScreenObserverIndex = (prunedList.push(baseView) - 1);
        }

        BaseView.fullScreenObservers = prunedList;
    }
}

Object.defineProperties(BaseView, {
    fullScreenObservers: { value: null, writable: true },
    fullScreenObserversCount: { value: 0, writable: true }
});

export default BaseView;
